import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useProductScope } from '../hooks/use-product-scope';

/** "This store" or "Shared" beside a card's title on the store product page. */
export function ScopeChip({ scope }: { scope: 'store' | 'shared' }) {
  const { t } = useTranslation('products');
  const { storeName, storeCount } = useProductScope();
  const hint =
    scope === 'store'
      ? t('scope.storeHint', { store: storeName })
      : t('scope.sharedHint', { count: storeCount ?? 0 });
  return (
    <span
      title={hint}
      className={cn(
        'rounded-full px-2 py-0.5 text-xs whitespace-nowrap',
        scope === 'store'
          ? 'bg-primary-tint text-primary font-medium'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {t(`scope.${scope}`)}
    </span>
  );
}

/** The Shared chip on the store page, nothing on the master page. */
export function SharedChip() {
  const { shared } = useProductScope();
  return shared ? <ScopeChip scope="shared" /> : null;
}

export function EditOnMasterLink({
  className,
  variantId,
}: {
  className?: string;
  /** Opens this variant's master page instead of the product's. */
  variantId?: string;
}) {
  const { t } = useTranslation('products');
  const { productId, shared, readOnly } = useProductScope();
  // Someone who may only read a shared card could do no more on the master.
  if (!productId || (shared && readOnly)) return null;
  if (variantId) {
    return (
      <Link
        to="/org/products/$productId/variants/$variantId"
        params={{ productId, variantId }}
        className={cn('text-primary text-[13px] hover:underline', className)}
      >
        {t('scope.editOnMaster')}
      </Link>
    );
  }
  return (
    <Link
      to="/org/products/$productId"
      params={{ productId }}
      className={cn('text-primary text-[13px] hover:underline', className)}
    >
      {t('scope.editOnMaster')}
    </Link>
  );
}

export interface CardEditActionProps {
  onEdit: () => void;
  /** Defaults to Edit. */
  label?: ReactNode;
  /** Hidden from people who may only read. */
  disabled?: boolean;
}

/** A card's title-row action: Edit, for anyone who may change the product. */
export function CardEditAction({ onEdit, label, disabled = false }: CardEditActionProps) {
  const { t } = useTranslation('products');
  const { readOnly } = useProductScope();
  if (readOnly || disabled) return null;
  return (
    <Button
      type="button"
      variant="link"
      size="xs"
      className="text-primary h-auto p-0 text-[13px]"
      onClick={onEdit}
    >
      {label ?? t('scope.edit')}
    </Button>
  );
}
